import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { theme } from "../../../styles/theme";
import { Col, Row, Div, Text } from "../../../styles/Common";
import { Card } from "../../../components/Card";
import { AiOutlineCheck, AiOutlineLeft } from "react-icons/ai";
import toast from "react-hot-toast";
import { Button } from "../../../components/Button";
import ModalCompleteLater from "./ModalCompleteLater";
import useGetListSurveys from "../hooks/useGetListSurveys";
import { getUserInfo, timeSkeleton } from "../../../utilities/helpers";
import useSaveSurveyAnswer from "../hooks/useSaveSurveyAnswer";
import ModalWarningSurvey from "./ModalWarningSurvey";
import { Skeleton } from "antd";
import useGetProfileUserSummaryMutation from "../hooks/useGetProfileUserSummaryMutation";
import { motion, AnimatePresence } from "framer-motion"; 

const DesktopFormSkeleton = ({ questionCount = 5, optionCount = 3 }) => {
    return (
      <Div direction="column" width="100%" m="0 0 40px 0">
        {/* Encabezado */}
        <Row 
          style={{ 
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            paddingBottom: "15px",
            marginBottom: "15px" 
          }}
        >
          <Col width={optionCount > 2 ? "35%" : "40%"}>
            <Skeleton.Input 
              active 
              size="default" 
              style={{ 
                width: '80%', 
                height: '24px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)' 
              }} 
            />
          </Col>
          <Col width={optionCount > 2 ? "65%" : "60%"}>
            <Row justify="space-around">
              {Array(optionCount).fill().map((_, i) => (
                <Div 
                  key={`skeleton-option-header-${i}`}
                  style={{ 
                    flex: 1, 
                    textAlign: 'center',
                    maxWidth: "33%"
                  }}
                >
                  <Skeleton.Input 
                    active 
                    size="small" 
                    style={{ 
                      width: '70%', 
                      height: '20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)' 
                    }} 
                  />
                </Div>
              ))}
            </Row>
          </Col>
        </Row>
        
        {/* Preguntas y opciones */}
        {Array(questionCount).fill().map((_, qIndex) => (
          <Row 
            key={`skeleton-question-${qIndex}`} 
            style={{ 
              borderBottom: qIndex < questionCount - 1 ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
              paddingTop: "15px",
              paddingBottom: "15px",
            }}
          >
            <Col width={optionCount > 2 ? "35%" : "40%"}>
              <Skeleton.Input 
                active 
                size="default" 
                style={{ 
                  width: '90%', 
                  height: '16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  marginBottom: '8px' 
                }} 
              />
              {Math.random() > 0.5 && (
                <Skeleton.Input 
                  active 
                  size="default" 
                  style={{ 
                    width: '60%', 
                    height: '16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)' 
                  }} 
                />
              )}
            </Col>
            <Col width={optionCount > 2 ? "65%" : "60%"}>
              <Row justify="space-around">
                {Array(optionCount).fill().map((_, optIndex) => (
                  <Div 
                    key={`skeleton-radio-${qIndex}-${optIndex}`}
                    align="center"
                    justify="center"
                    style={{ 
                      flex: 1, 
                      textAlign: 'center',
                      maxWidth: "33%"
                    }}
                  >
                    <Div 
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: `2px solid rgba(255, 255, 255, 0.2)`,
                        backgroundColor: "transparent"
                      }} 
                    />
                  </Div>
                ))}
              </Row>
            </Col>
          </Row>
        ))}
      </Div>
    );
  };
  
const MobileFormSkeleton = ({ questionCount = 5, optionCount = 3 }) => {
return (
    <>
    <Skeleton.Input 
        active 
        size="large" 
        style={{ 
        width: '80%', 
        height: '28px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        marginBottom: '15px' 
        }} 
    />
    
    <Div direction="column" width="100%" m="0 0 40px 0">
        {Array(questionCount).fill().map((_, qIndex) => (
        <Div 
            key={`skeleton-question-mobile-${qIndex}`} 
            direction="column"
            width="96%"
            style={{ 
            borderBottom: "2px solid rgba(255, 255, 255, 0.28)",
            paddingTop: "15px",
            paddingBottom: "15px",
            alignSelf: "center"
            }}
        >
            <Skeleton.Input 
            active 
            size="default" 
            style={{ 
                width: '90%', 
                height: '20px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                marginBottom: '25px' 
            }} 
            />
            
            <Row style={{ flexWrap: 'wrap', gap: '10px', justifyContent: "center" }}>
            {Array(optionCount).fill().map((_, optIndex) => (
                <Div 
                key={`skeleton-option-mobile-${qIndex}-${optIndex}`}
                style={{
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    minWidth: '30%',
                    height: '36px',
                    marginBottom: '5px'
                }}
                >
                <Skeleton.Input 
                    active 
                    size="small" 
                    style={{ 
                    width: '70%', 
                    height: '14px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)' 
                    }} 
                />
                </Div>
            ))}
            </Row>
        </Div>
        ))}
    </Div>
    </>
);
};

const SurveyFormSkeleton = ({ isMobile, questionCount = 5, optionCount = 3 }) => {
return isMobile ? (
    <MobileFormSkeleton questionCount={questionCount} optionCount={optionCount} />
) : (
    <DesktopFormSkeleton questionCount={questionCount} optionCount={optionCount} />
);
};

export const Survey = ({ isMobile, setActiveKey, viewSurvey, originActiveKey, setEditMyAccount }) => {
    const { nameid } = getUserInfo();
    const [showModalCompleteLater, setShowModalCompleteLater ] = useState(false);
    const [showModalWarningSurvey, setShowModalWarningSurvey ] = useState(false);
    const [messageWarning, setMessageWarning] = useState("");
    const [statusSurvey, setStatusSurvey] = useState(152);
    const [dataSurvey, setDataSurveys] = useState({});
    const getSurvey = useGetListSurveys(Number(nameid), viewSurvey.surveyId);
    const saveSurveyAnswer = useSaveSurveyAnswer(Number(nameid), viewSurvey.surveyId, statusSurvey, 0);
    const [isLoading, setIsLoading] = useState(true);
    const { data: formSurvey } = getSurvey;
    const [dataUser, setDataUser] = useState(null);

    useEffect(() => {
        if(formSurvey?.data?.result?.length > 0) {
            let findUser = formSurvey.data.result.find(item => item.surveyId == viewSurvey.surveyId);
            setDataSurveys(findUser)
            setTimeout(() => {
                setIsLoading(false);
            }, timeSkeleton)
        }
    },[formSurvey])

    useEffect(() => {
        window.scrollTo(0, 0);
      }, []);
    
    const [responses, setResponses] = useState({});
    
    const form = useMemo(() => ({
        ...viewSurvey,
        questions: dataSurvey?.questions?.length > 0? dataSurvey.questions: [],
        options: dataSurvey?.options?.length > 0? dataSurvey.options: [],  
    }), [viewSurvey, dataSurvey]);
    
    const isSurveyComplete = useMemo(() => {
        return form.questions.every(question => responses[question.id]);
    }, [form.questions, responses]);

    const handleRadioChange = (question, option) => {
        setResponses(prev => ({
            ...prev,
            [question.id]: {question, option}
        }));
    };

    const getProfileUserSummaryMutation = useGetProfileUserSummaryMutation();

    useEffect(() => {
        let initialResponses = {};
        if(dataSurvey?.answersJson) {
            initialResponses = JSON.parse(dataSurvey?.answersJson);
        }
        setResponses(initialResponses);
        if (dataSurvey?.categoryName == "PulpeyBag" || dataSurvey?.categoryName == "PulpeyBox") {
            getProfileUserSummaryMutation.mutate(Number(nameid),
            {
                onSuccess: (res) => {
                    if(res?.status == 200) {
                        console.log(res.data.result, 'HOLAAAAAAAAAasjdfakjsdfkjajkfdajksdfjkasdkfjajksdfkj');
                        setDataUser(res.data.result);
                    }
                },
                onError: (err) => {
                },
            }
            );
        }
    }, [dataSurvey]);

    
    const handleSaveSurvey = async () => {
        let completeForm = true; 
        let errQuestion = []; 
        form.questions.forEach((item, index) => {
            if (!responses[item.id]) {
                completeForm = false;
                errQuestion.push(index + 1); 
            }
        });
        if (completeForm) {
            saveSurveyAnswer.reset();
            saveSurveyAnswer.mutate(
                responses,
                {
                    onSuccess: (res) => {
                        toast.success(res?.data?.message || "Respuestas guardadas correctamente");
                        setActiveKey(originActiveKey);
                    },
                    onError: (err) => {
                        if (err?.response?.data?.errors) {
                            const errorMessages = Object.values(err.response.data.errors)
                              .flat()
                              .join(", ");
                            toast.error(errorMessages);
                          }
                    }
                }
            );
        } else {
            setMessageWarning(`Es necesario contestar la(s) pregunta(s): ${errQuestion.join(', ')}`);
            setShowModalWarningSurvey(true);
        }
    };

    const handleSaveLaterSurvey = () => {
        setShowModalCompleteLater(true);
        setStatusSurvey(165);
    };

    
    const DesktopView = () => (
        <Div direction="column" width="100%" m="0 0 40px 0">          
            <Row 
                style={{ 
                    borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                    paddingBottom: "15px",
                    marginBottom: "15px" 
                }}
            >
                <Col width={form.options.length > 2? "35%": "40%"}>
                    <Text 
                        color={theme.colors.white} 
                        size={theme.fonts.size.h5}
                        weight={theme.fonts.weight.semibold}
                    >
                        {form.title}
                    </Text>
                </Col>
                <Col width={form.options.length > 2? "65%": "60%"}>
                    <Row justify="space-around">
                        {form.options.map((option, i) => (
                            <Text 
                                key={`option-header-${i}`}
                                color={theme.colors.white} 
                                size={theme.fonts.size.h6}
                                weight={theme.fonts.weight.semibold}
                                style={{ 
                                    flex: 1, 
                                    textAlign: 'center',
                                    maxWidth: "33%"
                                }}
                            >
                                {option.answer}
                            </Text>
                        ))}
                    </Row>
                </Col>
            </Row>
            
            
            {form.questions.map((item, index) => (
                <Row 
                    key={`question-${index}`} 
                    style={{ 
                        borderBottom: index < form.questions.length - 1 ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                        paddingTop: "15px",
                        paddingBottom: "15px",
                    }}
                >
                    <Col width={form.options.length > 2? "35%": "40%"} >
                        <Text
                            color={theme.colors.white}
                            size={theme.fonts.size.p}
                        >
                            {index + 1}. {item.question}
                        </Text>
                    </Col>
                    <Col width={form.options.length > 2? "65%": "60%"} >
                        <Row justify="space-around">
                            {form.options.map((option, optionIndex) => (
                                <Div 
                                    key={`radio-${index}-${optionIndex}`}
                                    align="center"
                                    justify="center"
                                    style={{ 
                                        flex: 1, 
                                        textAlign: 'center',
                                        maxWidth: "33%"
                                    }}
                                >
                                    <label style={{ 
                                        display: 'inline-block',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        border: `2px solid ${theme.colors.red || "#FF3E41"}`,
                                        backgroundColor: responses[item.id]?.option?.id === option.id ? theme.colors.red || "#FF3E41" : "transparent",
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}>
                                        <input 
                                            type="radio"
                                            name={`question-${item.id}`}
                                            value={option.id}
                                            checked={responses[item.id]?.option?.id === option.id}
                                            onChange={() => handleRadioChange(item, option)}
                                            style={{ 
                                                opacity: 0,
                                                position: 'absolute',
                                                width: '100%',
                                                height: '100%',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    </label>
                                </Div>
                            ))}
                        </Row>
                    </Col>
                </Row>
            ))}
        </Div>
    );
        
    
    const MobileView = () => (
        <>
            <Text 
                color={theme.colors.white} 
                size={theme.fonts.size.h4}
                weight={theme.fonts.weight.semibold}
                mb="15px"
            >
                {form.title}
            </Text>
            
            <Div direction="column" width="100%" m="0 0 40px 0">
                {form.questions.map((item, index) => {
                    const selectedOptionId = responses[item.id]?.option?.id;
                    
                    return (
                        <Div 
                            key={`question-mobile-${index}`} 
                            direction="column"
                            width="96%"
                            style={{ 
                                borderBottom: "2px solid rgba(255, 255, 255, 0.28)",
                                paddingTop: "15px",
                                paddingBottom: "15px",
                                alignSelf: "center"
                            }}
                        >
                            <Text
                                color={theme.colors.white}
                                size={theme.fonts.size.h6}
                                weight={theme.fonts.weight.bold}
                                mb="25px"
                            >
                                {index + 1}. {item.question}
                            </Text>
                            
                            <Row style={{ flexWrap: 'wrap', gap: '10px', justifyContent: "center" }}>
                                {form.options.map((option, optionIndex) => {
                                    const isSelected = selectedOptionId === option.id;
                                    
                                    return (
                                        <Div 
                                            key={`radio-mobile-${index}-${optionIndex}`}
                                            style={{
                                                backgroundColor: isSelected ? 'rgba(255, 62, 65, 0.1)' : 'transparent',
                                                border: `1px solid ${isSelected ? theme.colors.red || "#FF3E41" : "rgba(255, 255, 255, 0.2)"}`,
                                                borderRadius: '8px',
                                                padding: '8px 12px',
                                                minWidth: '30%',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: '5px'
                                            }}
                                            onClick={() => handleRadioChange(item, option)}
                                        >
                                            <Text
                                                color={theme.colors.white}
                                                size={theme.fonts.size.p}
                                                style={{marginRight: '5px'}}
                                            >
                                                {option.answer}
                                            </Text>
                                            
                                            {isSelected && (
                                                <AiOutlineCheck color={theme.colors.red || "#FF3E41"} size={16} />
                                            )}
                                        </Div>
                                    );
                                })}
                            </Row>
                        </Div>
                    );
                })}
            </Div>
        </>
    );
    

    const handleCloseModal = () => {
        setShowModalCompleteLater(false);
        setStatusSurvey(152);
    };

    const handleContinueLater = () => {
        let questionCount = 0; 
        form.questions.forEach((item, index) => {
            if (responses[item.id]) {
                questionCount++ 
            }
        });
        if(questionCount > 0) {
            saveSurveyAnswer.reset();
            saveSurveyAnswer.mutate(
                responses,
                {
                    onSuccess: (res) => {
                        toast.success(res?.data?.message || "Respuestas guardadas correctamente");
                        setActiveKey(originActiveKey);
                    },
                    onError: (err) => {
                        if (err?.response?.data?.errors) {
                            const errorMessages = Object.values(err.response.data.errors)
                              .flat()
                              .join(", ");
                            toast.error(errorMessages);
                          }
                    }
                }
            );
        } else {
            toast.error("Para guardar su progreso, responda al menos una pregunta.")
            setShowModalCompleteLater(false);
        }
    }

    return (
        <>
            <ModalCompleteLater 
                showModal={showModalCompleteLater}
                handleCloseModal={handleCloseModal}
                handleContinue={handleContinueLater}
                isMobile={isMobile}
            />
            <ModalWarningSurvey 
                showModal={showModalWarningSurvey}
                handleCloseModal={() => {setShowModalWarningSurvey(false)}}
                isMobile={isMobile}
                description={messageWarning}
            />
            <Div
                direction={!isMobile ? "row" : "column"}
                gap="17px"
                m={!isMobile ? "49px 0 136px 0" : "25px 0 150px 0"}
                align={isMobile && "center"}
                justify="center"
            >
                <Col width={!isMobile ? "96%" : "90%"}>
                    <Row
                        p={isMobile ? "0px 0px 0px 0px" : "0px 0px 0px 38px"}
                    >
                        <Text
                            color={theme.colors.red}
                            weight={theme.fonts.weight.semibold}
                            size={theme.fonts.size.h6}
                            mb="20px"
                            style={{ display: 'flex', alignItems: 'center', cursor: "pointer" }}
                            onClick={() => {
                                setActiveKey(originActiveKey);
                            }}
                        ><AiOutlineLeft style={{ marginRight: '15px' }} size={20} />Volver</Text>
                    </Row>
                    
                    <Card
                        width={!isMobile ? "95%" : "100%"}
                        style={{ 
                            alignSelf: 'center', 
                        }}
                        p={!isMobile? "40px 35px 0px 38px": "50px 0 0 0"}
                    >
                        <Row
                            p={isMobile && "0px 20px 0px 20px"}
                        >
                            <Text
                                color={theme.colors.white}
                                size={theme.fonts.size.h3}
                                mb="24px"
                            >
                               {viewSurvey?.name || "Encuesta"} {/* - {viewSurvey?.categoryName || "Preferencias"} */}
                            </Text>
                        </Row>
                        
                        <Row 
                            m="0 0 40px 0"
                            p={isMobile && "0px 20px 0px 20px"}
                        >
                            <Text
                                weight={theme.fonts.weight.semibold}
                                color={theme.colors.white}
                                size={theme.fonts.size.h6}
                                style={{ lineHeight: "30px" }}
                            >
                                {viewSurvey?.description || `Completa la encuesta y permite que las marcas obtengan la mayor 
                                información sobre ti para poder personalizar la PulpeyBox de 
                                la mejor manera, además acumulas puntos que pronto podrás redimir.`} 
                            </Text>
                        </Row>

                        {(dataSurvey?.categoryName === "PulpeyBag" || dataSurvey?.categoryName === "PulpeyBox") && dataUser && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                {/* Header */}
                                <Row
                                    align="center"
                                    gap="8px"
                                    style={{ 
                                        margin: isMobile ? "0 16px 16px 16px" : "0 24px 20px 24px",
                                        paddingBottom: "12px",
                                        borderBottom: "1px solid rgba(255,255,255,0.1)"
                                    }}
                                >
                                    <Text
                                        weight={theme.fonts.weight.bold}
                                        color={theme.colors.white}
                                        size={isMobile ? theme.fonts.size.h6 : theme.fonts.size.h5}
                                        style={{ letterSpacing: "0.2px" }}
                                    >
                                        🧾 Datos registrados
                                    </Text>
                                </Row>

                                {/* Contenedor principal */}
                                <Div
                                    m={isMobile ? "0 16px 24px 16px" : "0 24px 32px 24px"}
                                    direction="column"
                                    style={{
                                        backgroundColor: "rgba(255,255,255,0.08)",
                                        borderRadius: "12px",
                                        padding: isMobile ? "16px" : "20px",
                                        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                                        backdropFilter: "blur(8px)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    {/* Grid responsive */}
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                                            gap: isMobile ? "12px" : "16px 24px",
                                            color: theme.colors.white,
                                        }}
                                    >
                                        {/* Nombre */}
                                        <div>
                                            <Text 
                                                size={theme.fonts.size.small} 
                                                color="rgba(255,255,255,0.7)" 
                                                style={{ marginBottom: "4px" }}
                                            >
                                                NOMBRE
                                            </Text>
                                            <Text weight={theme.fonts.weight.medium} color={theme.colors.white}>
                                                {dataUser.name || "-"}
                                            </Text>
                                        </div>
                                        
                                        {/* Apellido */}
                                        <div>
                                            <Text 
                                                size={theme.fonts.size.small} 
                                                color="rgba(255,255,255,0.7)" 
                                                style={{ marginBottom: "4px" }}
                                            >
                                                APELLIDO
                                            </Text>
                                            <Text weight={theme.fonts.weight.medium} color={theme.colors.white}>
                                                {dataUser.lastName || "-"}
                                            </Text>
                                        </div>
                                        
                                        {/* Correo - full width en móvil, normal en desktop */}
                                        <div style={{ gridColumn: isMobile ? "span 1" : "span 1" }}>
                                            <Text 
                                                size={theme.fonts.size.small} 
                                                color="rgba(255,255,255,0.7)" 
                                                style={{ marginBottom: "4px" }}
                                            >
                                                CORREO
                                            </Text>
                                            <Text 
                                                weight={theme.fonts.weight.medium} 
                                                color={theme.colors.white} 
                                                style={{ 
                                                    wordBreak: "break-all",
                                                    fontSize: isMobile ? theme.fonts.size.small : theme.fonts.size.base
                                                }}
                                            >
                                                {dataUser.email || "-"}
                                            </Text>
                                        </div>
                                        
                                        {/* Teléfono */}
                                        <div>
                                            <Text 
                                                size={theme.fonts.size.small} 
                                                color="rgba(255,255,255,0.7)" 
                                                style={{ marginBottom: "4px" }}
                                            >
                                                TELÉFONO
                                            </Text>
                                            <Text weight={theme.fonts.weight.medium} color={theme.colors.white}>
                                                {dataUser.phoneNumber || "-"}
                                            </Text>
                                        </div>
                                        
                                        {/* Dirección - siempre full width */}
                                        <div style={{ gridColumn: "1 / -1" }}>
                                            <Text 
                                                size={theme.fonts.size.small} 
                                                color="rgba(255,255,255,0.7)" 
                                                style={{ marginBottom: "4px" }}
                                            >
                                                DIRECCIÓN DE ENTREGA
                                            </Text>
                                            <Text 
                                                weight={theme.fonts.weight.medium} 
                                                color={theme.colors.white} 
                                                style={{ 
                                                    lineHeight: "1.4",
                                                    fontSize: isMobile ? theme.fonts.size.small : theme.fonts.size.base
                                                }}
                                            >
                                                {dataUser.address || "-"}
                                            </Text>
                                        </div>
                                        
                                        {/* Instrucciones - siempre full width */}
                                        <div style={{ gridColumn: "1 / -1" }}>
                                            <Text 
                                                size={theme.fonts.size.small} 
                                                color="rgba(255,255,255,0.7)" 
                                                style={{ marginBottom: "4px" }}
                                            >
                                                INSTRUCCIONES ESPECIALES
                                            </Text>
                                            <Text 
                                                weight={theme.fonts.weight.medium} 
                                                color={theme.colors.white} 
                                                style={{ 
                                                    lineHeight: "1.4",
                                                    fontSize: isMobile ? theme.fonts.size.small : theme.fonts.size.base
                                                }}
                                            >
                                                {dataUser.instruction || "-"}
                                            </Text>
                                        </div>
                                    </div>

                                    {/* Botón - centrado en móvil, a la derecha en desktop */}
                                    <div style={{ 
                                        display: "flex", 
                                        justifyContent: isMobile ? "center" : "flex-end", 
                                        marginTop: "20px",
                                        paddingTop: "16px",
                                        borderTop: "1px solid rgba(255,255,255,0.05)"
                                    }}>
                                        <motion.div
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <Button
                                                onClick={() => {
                                                    setActiveKey("2");
                                                    setEditMyAccount(true);
                                                }}
                                                style={{
                                                    backgroundColor: "rgba(255,255,255,0.12)",
                                                    border: "1px solid rgba(255,255,255,0.15)",
                                                    borderRadius: "8px",
                                                    padding: isMobile ? "8px 16px" : "10px 20px",
                                                    color: theme.colors.white,
                                                    fontSize: isMobile ? theme.fonts.size.small : theme.fonts.size.base,
                                                    fontWeight: theme.fonts.weight.medium,
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "6px",
                                                    minWidth: isMobile ? "140px" : "160px"
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.18)";
                                                    e.currentTarget.style.border = "1px solid rgba(255,255,255,0.25)";
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.12)";
                                                    e.currentTarget.style.border = "1px solid rgba(255,255,255,0.15)";
                                                }}
                                            >
                                                <span>✏️</span>
                                                Actualizar información
                                            </Button>
                                        </motion.div>
                                    </div>
                                </Div>
                            </motion.div>
                        )}

                        {isLoading ? (
                            <SurveyFormSkeleton 
                                isMobile={isMobile} 
                                questionCount={5} 
                                optionCount={form?.options?.length || 3}  
                            />
                        ) : (
                            <>
                                {isMobile ? <MobileView /> : <DesktopView />}
                            </>
                        )}
                        
                        {!isLoading && <Row m="0 0 51px 0" gap="18px" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                            {!isSurveyComplete && (
                                <Button 
                                    width={!isMobile ? "180px" : "170px"} 
                                    height="40px" 
                                    variant="outlined" 
                                    onClick={handleSaveLaterSurvey}
                                >
                                    Completar después
                                </Button>
                            )}
                            <Button width={!isMobile ? "180px" : "170px"} height="40px" onClick={handleSaveSurvey}>
                                Guardar información
                            </Button>
                        </Row>}
                    </Card>
                </Col>
            </Div>
        </>
    );
};

Survey.propTypes = {
    isMobile: PropTypes.bool,
    setActiveKey: PropTypes.func,
    viewSurvey: PropTypes.object,
    originActiveKey: PropTypes.string
};